import React from "react";
import {
  Card,
  Elevation,
  Tag,
  Button,
  H1,
  H3,
  ControlGroup,
  InputGroup,
  NumericInput,
  Intent,
  NonIdealState,
  Spinner,
  MenuItem,
} from "@blueprintjs/core";
import { DateInput } from "@blueprintjs/datetime";
import { MultiSelect } from "@blueprintjs/select";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-enterprise";
import { inject, observer } from "mobx-react";
import { apiCallPost, apiCallGet } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import _ from "lodash";
import moment from "moment";
import SingleIDSelectWrapper from "../../../micro/utils/SingleIDSelectWrapper";
import SingleSelectWrapper from "../../../micro/utils/SingleSelectWrapper";

// Set AG Grid license
LicenseManager.setLicenseKey("");

// Generate 30-minute interval time options from 8:00 AM to 6:00 PM
const generateTimeOptions = () => {
  const start = moment("07:00 AM", "hh:mm A");
  const end = moment("07:00 PM", "hh:mm A");
  const options = [];
  let cursor = start.clone();
  while (cursor.isSameOrBefore(end)) {
    options.push(cursor.format("h:mm A"));
    cursor.add(30, "minutes");
  }
  return options;
};

class DoctorScheduleMaster extends React.Component {
  state = {
    // Data
    schedules: [],
    doctors: [],
    branches: [],
    groups: [],

    // Loading states
    isLoading: false,
    isSaving: false,

    // Grid state
    gridApi: null,
    columnApi: null,
    quickFilterText: "",

    // Form state for adding new schedule
    newSchedule: {
      doctorId: null,
      doctorName: "",
      branchName: "",
      day: "",
      timeFrom: "",
      timeTo: "",
      surgeryCount: 0,
      effectiveFrom: null,
      effectiveTo: null,
      surgeryGroups: [],
    },

    // Form reset key to force component re-render
    formResetKey: 0,

    // Edit modal state
    isEditModalOpen: false,
    editingSchedule: null,

    // Days of the week
    daysOfWeek: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],

    // Time options (30-minute intervals 8:00 AM to 6:00 PM)
    timeOptions: generateTimeOptions(),
  };

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    try {
      this.setState({ isLoading: true });

      // Load schedules, doctors, branches, and groups in parallel
      await Promise.all([this.loadSchedules(), this.loadDoctors(), this.loadBranches(), this.loadGroups()]);
    } catch (err) {
      cogoToast.error("Failed to load data");
      console.error(err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadGroups = async () => {
    try {
      cogoToast.loading("Loading surgery groups...");
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxSurgeryGroupMaster/getAll`, {});
      const groups = (response || []).map((g) => ({ _id: g._id, groupName: g.groupName }));
      this.setState({ groups });
      cogoToast.success("Surgery groups loaded");
    } catch (err) {
      console.error("Failed to load surgery groups:", err);
      // Fallback to dummy groups
      this.setState({
        groups: [
          { _id: "g1", groupName: "Glaucoma" },
          { _id: "g2", groupName: "Oculoplasty" },
          { _id: "g3", groupName: "Cornea" },
          { _id: "g4", groupName: "Retina" },
          { _id: "g5", groupName: "LASIK" },
        ],
      });
    }
  };

  loadSchedules = async () => {
    try {
      cogoToast.loading("Loading doctor schedules...");
      const res = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxDoctorScheduleMaster/getAll`, {});
      const schedules = (res || []).map((s) => {
        // Backward compatibility: if timeFrom/timeTo missing, derive from timeFrame
        if ((!s.timeFrom || !s.timeTo) && s.timeFrame && typeof s.timeFrame === "string") {
          const parts = s.timeFrame.split("-");
          if (parts.length === 2) {
            const from = parts[0].trim();
            const to = parts[1].trim();
            return { ...s, timeFrom: from, timeTo: to };
          }
        }
        return s;
      });
      this.setState({ schedules });
      cogoToast.success("Doctor schedules loaded successfully");
    } catch (err) {
      cogoToast.error("Failed to load doctor schedules");
      console.error("Failed to load schedules:", err);
      // Initialize with dummy data if collection doesn't exist
      this.initializeDummyData();
    }
  };

  loadDoctors = async () => {
    try {
      const res = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllConsultants`, {});
      this.setState({
        doctors: _.sortBy(res, "name").map((doc) => ({
          ...doc,
          searchName: `${doc.shortcode || ""} | ${doc.name}`,
        })),
      });
    } catch (err) {
      console.error("Failed to load doctors:", err);
      // Fallback to dummy doctors
      this.setState({
        doctors: [
          { _id: "1", name: "Dr. Ammar", shortcode: "AMM" },
          { _id: "2", name: "Dr. Smith", shortcode: "SMT" },
          { _id: "3", name: "Dr. Johnson", shortcode: "JOH" },
        ].map((doc) => ({
          ...doc,
          searchName: `${doc.shortcode} | ${doc.name}`,
        })),
      });
    }
  };

  loadBranches = async () => {
    try {
      const res = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});
      this.setState({ branches: res || [] });
    } catch (err) {
      console.error("Failed to load branches:", err);
      // Fallback to dummy branches
      this.setState({
        branches: ["Newtown Branch", "Park Street Branch", "Salt Lake Branch", "Howrah Branch"],
      });
    }
  };

  initializeDummyData = () => {
    const dummySchedules = [
      {
        _id: "1",
        doctorId: "1",
        doctorName: "Dr. Ammar",
        branchName: "Delhi",
        day: "Wednesday",
        timeFrom: "10:00 AM",
        timeTo: "3:00 PM",
        timeFrame: "10:00 AM - 3:00 PM",
        surgeryCount: 6,
        effectiveFrom: "2025-09-01",
        effectiveTo: "2025-12-31",
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "2",
        doctorId: "1",
        doctorName: "Dr. Ammar",
        branchName: "Mumbai",
        day: "Friday",
        timeFrom: "9:00 AM",
        timeTo: "12:00 PM",
        timeFrame: "9:00 AM - 12:00 PM",
        surgeryCount: 4,
        effectiveFrom: "2025-09-01",
        effectiveTo: "2025-12-31",
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];

    this.setState({ schedules: dummySchedules });
  };

  onGridReady = (params) => {
    this.setState({ gridApi: params.api, columnApi: params.columnApi });
  };

  columnDefs = [
    {
      headerName: "Doctor",
      field: "doctorName",
      width: 150,
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Branch",
      field: "branchName",
      width: 150,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Day",
      field: "day",
      width: 100,
      filter: "agSetColumnFilter",
      filterParams: {
        values: this.state.daysOfWeek,
      },
    },
    {
      headerName: "Surgery Groups",
      field: "surgeryGroups",
      width: 220,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => (Array.isArray(params.value) ? params.value.join(", ") : params.value || ""),
    },
    {
      headerName: "Time From",
      field: "timeFrom",
      width: 120,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Time To",
      field: "timeTo",
      width: 120,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Surgery Count",
      field: "surgeryCount",
      width: 120,
      filter: "agNumberColumnFilter",
      cellRenderer: (params) => params.value || 0,
    },
    {
      headerName: "Effective From",
      field: "effectiveFrom",
      width: 130,
      filter: "agDateColumnFilter",
      cellRenderer: (params) => (params.value ? moment(params.value).format("DD/MM/YYYY") : ""),
    },
    {
      headerName: "Effective To",
      field: "effectiveTo",
      width: 130,
      filter: "agDateColumnFilter",
      cellRenderer: (params) => (params.value ? moment(params.value).format("DD/MM/YYYY") : ""),
    },
    {
      headerName: "Created By",
      field: "createdByName",
      width: 150,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => params.value || "—",
    },
    {
      headerName: "Created At",
      field: "createdAt",
      width: 165,
      filter: "agDateColumnFilter",
      cellRenderer: (params) =>
        params.value ? moment(params.value).format("DD/MM/YY HH:mm") : "—",
    },
    {
      headerName: "Updated By",
      field: "updatedByName",
      width: 150,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => params.value || "—",
    },
    {
      headerName: "Updated At",
      field: "updatedAt",
      width: 165,
      filter: "agDateColumnFilter",
      cellRenderer: (params) =>
        params.value ? moment(params.value).format("DD/MM/YY HH:mm") : "—",
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      cellRenderer: (params) => {
        const container = document.createElement("div");
        const editBtn = document.createElement("button");
        const deleteBtn = document.createElement("button");

        editBtn.innerHTML = "✏️";
        editBtn.style.marginRight = "5px";
        editBtn.style.padding = "4px 8px";
        editBtn.style.border = "1px solid #137cbd";
        editBtn.style.backgroundColor = "#137cbd";
        editBtn.style.color = "white";
        editBtn.style.borderRadius = "3px";
        editBtn.style.cursor = "pointer";
        editBtn.onclick = () => this.editSchedule(params.data);

        deleteBtn.innerHTML = "🗑️";
        deleteBtn.style.padding = "4px 8px";
        deleteBtn.style.border = "1px solid #db3737";
        deleteBtn.style.backgroundColor = "#db3737";
        deleteBtn.style.color = "white";
        deleteBtn.style.borderRadius = "3px";
        deleteBtn.style.cursor = "pointer";
        deleteBtn.onclick = () => this.deleteSchedule(params.data._id);

        container.appendChild(editBtn);
        container.appendChild(deleteBtn);

        return container;
      },
      filter: false,
      sortable: false,
    },
  ];

  addSchedule = async () => {
    const { newSchedule } = this.state;

    // Validation
    if (
      !newSchedule.doctorId ||
      !newSchedule.branchName ||
      !newSchedule.day ||
      !newSchedule.timeFrom ||
      !newSchedule.timeTo ||
      !newSchedule.effectiveFrom ||
      !newSchedule.effectiveTo
    ) {
      cogoToast.error("Please fill in all required fields");
      return;
    }

    // Validate time range
    if (newSchedule.timeFrom && newSchedule.timeTo) {
      const fromT = moment(newSchedule.timeFrom, "h:mm A");
      const toT = moment(newSchedule.timeTo, "h:mm A");
      if (toT.isBefore(fromT)) {
        cogoToast.error("Time To cannot be before Time From");
        return;
      }
    }

    // Validate date range
    if (newSchedule.effectiveFrom && newSchedule.effectiveTo) {
      const fromDate = moment(newSchedule.effectiveFrom);
      const toDate = moment(newSchedule.effectiveTo);

      if (toDate.isBefore(fromDate)) {
        cogoToast.error("Effective To date cannot be before Effective From date");
        return;
      }
    }

    // Reset form fields immediately after validation passes
    const resetForm = () => {
      this.setState({
        newSchedule: {
          doctorId: null,
          doctorName: "",
          branchName: "",
          day: "",
          timeFrom: "",
          timeTo: "",
          surgeryCount: 0,
          effectiveFrom: null,
          effectiveTo: null,
          surgeryGroups: [],
        },
        formResetKey: this.state.formResetKey + 1,
      });
    };

    try {
      cogoToast.loading("Adding doctor schedule...");
      this.setState({ isSaving: true });

      const scheduleData = {
        ...newSchedule,
        timeFrame: `${newSchedule.timeFrom} - ${newSchedule.timeTo}`,
        surgeryGroups: newSchedule.surgeryGroups || [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const res = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxDoctorScheduleMaster/createNew`,
        scheduleData
      );

      // Add to local state (ensure timeFrom/timeTo present for UI)
      this.setState((prevState) => ({
        schedules: [
          ...prevState.schedules,
          {
            ...res,
            timeFrom: newSchedule.timeFrom,
            timeTo: newSchedule.timeTo,
            surgeryGroups: newSchedule.surgeryGroups || [],
          },
        ],
      }));

      cogoToast.success("Doctor schedule added successfully");

      // Log audit
      this.logAudit("CREATE", res, null);
    } catch (err) {
      cogoToast.error("Failed to add doctor schedule");
      console.error("Detailed error:", err);
    } finally {
      this.setState({ isSaving: false });
      // Reset form fields after API call (success or failure)
      resetForm();
    }
  };

  editSchedule = (schedule) => {
    // Open edit modal with prefilled values
    this.setState({
      isEditModalOpen: true,
      editingSchedule: { ...schedule },
    });
  };

  updateSchedule = async () => {
    const { editingSchedule } = this.state;

    // Validation
    if (
      !editingSchedule.doctorId ||
      !editingSchedule.branchName ||
      !editingSchedule.day ||
      !editingSchedule.timeFrom ||
      !editingSchedule.timeTo ||
      !editingSchedule.effectiveFrom ||
      !editingSchedule.effectiveTo
    ) {
      cogoToast.error("Please fill in all required fields");
      return;
    }

    // Validate time range
    if (editingSchedule.timeFrom && editingSchedule.timeTo) {
      const fromT = moment(editingSchedule.timeFrom, "h:mm A");
      const toT = moment(editingSchedule.timeTo, "h:mm A");
      if (toT.isBefore(fromT)) {
        cogoToast.error("Time To cannot be before Time From");
        return;
      }
    }

    // Validate date range
    if (editingSchedule.effectiveFrom && editingSchedule.effectiveTo) {
      const fromDate = moment(editingSchedule.effectiveFrom);
      const toDate = moment(editingSchedule.effectiveTo);

      if (toDate.isBefore(fromDate)) {
        cogoToast.error("Effective To date cannot be before Effective From date");
        return;
      }
    }

    try {
      cogoToast.loading("Updating doctor schedule...");
      this.setState({ isSaving: true });

      const scheduleData = {
        ...editingSchedule,
        timeFrame: `${editingSchedule.timeFrom} - ${editingSchedule.timeTo}`,
        surgeryGroups: editingSchedule.surgeryGroups || [],
        updatedAt: new Date(),
      };

      const res = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxDoctorScheduleMaster/update`,
        scheduleData
      );

      // Update local state
      this.setState((prevState) => ({
        schedules: prevState.schedules.map((s) => {
          if (s._id !== editingSchedule._id) return s;
          const merged = { ...s, ...res, updatedAt: new Date() };
          if (!merged.timeFrom) merged.timeFrom = editingSchedule.timeFrom;
          if (!merged.timeTo) merged.timeTo = editingSchedule.timeTo;
          if (!merged.surgeryGroups) merged.surgeryGroups = editingSchedule.surgeryGroups || [];
          return merged;
        }),
        isEditModalOpen: false,
        editingSchedule: null,
      }));

      cogoToast.success("Doctor schedule updated successfully");

      // Log audit
      this.logAudit("UPDATE", res, editingSchedule);
    } catch (err) {
      cogoToast.error("Failed to update doctor schedule");
      console.error("Detailed error:", err);
    } finally {
      this.setState({ isSaving: false });
    }
  };

  closeEditModal = () => {
    this.setState({
      isEditModalOpen: false,
      editingSchedule: null,
    });
  };

  deleteSchedule = async (scheduleId) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) {
      return;
    }

    try {
      cogoToast.loading("Deleting doctor schedule...");
      await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxDoctorScheduleMaster/delete`, { _id: scheduleId });

      // Remove from local state
      this.setState((prevState) => ({
        schedules: prevState.schedules.filter((s) => s._id !== scheduleId),
      }));

      cogoToast.success("Doctor schedule deleted successfully");

      // Log audit
      this.logAudit("DELETE", { _id: scheduleId }, null);
    } catch (err) {
      cogoToast.error("Failed to delete doctor schedule");
      console.error("Detailed error:", err);
    }
  };

  logAudit = (action, newData, oldData) => {
    const auditLog = {
      action,
      entityType: "neoSxDoctorScheduleMaster",
      entityId: newData._id,
      newData,
      oldData,
      userId: this.props.authStore.id,
      userName: this.props.authStore.name,
      timestamp: new Date(),
    };

    console.log("Audit Log:", auditLog);
    // In a real implementation, this would be sent to an audit service
  };

  isDateRangeValid = () => {
    const { effectiveFrom, effectiveTo } = this.state.newSchedule;
    if (!effectiveFrom || !effectiveTo) return true; // Valid if either is empty
    return moment(effectiveTo).isSameOrAfter(moment(effectiveFrom));
  };

  isTimeRangeValid = () => {
    const { timeFrom, timeTo } = this.state.newSchedule;
    if (!timeFrom || !timeTo) return true; // Valid if either is empty
    return !moment(timeTo, "h:mm A").isBefore(moment(timeFrom, "h:mm A"));
  };

  isEditDateRangeValid = () => {
    const { editingSchedule } = this.state;
    if (!editingSchedule) return true;
    const { effectiveFrom, effectiveTo } = editingSchedule;
    if (!effectiveFrom || !effectiveTo) return true; // Valid if either is empty
    return moment(effectiveTo).isSameOrAfter(moment(effectiveFrom));
  };

  isEditTimeRangeValid = () => {
    const { editingSchedule } = this.state;
    if (!editingSchedule) return true;
    const { timeFrom, timeTo } = editingSchedule;
    if (!timeFrom || !timeTo) return true; // Valid if either is empty
    return !moment(timeTo, "h:mm A").isBefore(moment(timeFrom, "h:mm A"));
  };

  // MultiSelect helpers for Surgery Groups
  filterGroupItems = (query, groupName) => {
    if (!groupName) return false;
    return groupName.toLowerCase().includes((query || "").toLowerCase());
  };

  renderGroupItem = (groupName, { handleClick, modifiers }) => {
    if (!modifiers?.matchesPredicate) {
      return null;
    }
    return (
      <MenuItem
        key={groupName}
        text={groupName}
        onClick={handleClick}
        active={modifiers.active}
        disabled={modifiers.disabled}
        style={{ minWidth: "300px" }}
      />
    );
  };

  renderGroupTag = (groupName) => groupName;

  renderAddForm = () => {
    const { newSchedule, doctors, branches, daysOfWeek, timeOptions, formResetKey } = this.state;
    const isDateRangeValid = this.isDateRangeValid();
    const isTimeRangeValid = this.isTimeRangeValid();

    return (
      <Card elevation={Elevation.TWO} style={{ marginBottom: "20px", padding: "20px" }}>
        <H3 style={{ color: "#f5f8fa", marginBottom: "20px" }}>Add New Schedule</H3>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
          <ControlGroup>
            <Tag large minimal>
              Doctor *
            </Tag>
            <SingleIDSelectWrapper
              key={`doctor-${formResetKey}`}
              items={doctors}
              selectedItem={doctors.find((d) => d._id === newSchedule.doctorId)}
              labelKey="searchName"
              onChange={(item) => {
                this.setState({
                  newSchedule: {
                    ...newSchedule,
                    doctorId: item._id,
                    doctorName: item.name,
                  },
                });
              }}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              Branch *
            </Tag>
            <SingleSelectWrapper
              key={`branch-${formResetKey}`}
              items={branches}
              selectedItem={newSchedule.branchName}
              onChange={(item) => {
                this.setState({
                  newSchedule: {
                    ...newSchedule,
                    branchName: item,
                  },
                });
              }}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              Day *
            </Tag>
            <SingleSelectWrapper
              key={`day-${formResetKey}`}
              items={daysOfWeek}
              selectedItem={newSchedule.day}
              onChange={(item) => {
                this.setState({
                  newSchedule: {
                    ...newSchedule,
                    day: item,
                  },
                });
              }}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              Time From *
            </Tag>
            <SingleSelectWrapper
              key={`timeFrom-${formResetKey}`}
              items={timeOptions}
              selectedItem={newSchedule.timeFrom}
              onChange={(item) => {
                // If new Time From is after current Time To, clear Time To
                let updatedTimeTo = newSchedule.timeTo;
                if (updatedTimeTo) {
                  const fromT = moment(item, "h:mm A");
                  const toT = moment(updatedTimeTo, "h:mm A");
                  if (toT.isBefore(fromT)) {
                    updatedTimeTo = "";
                    cogoToast.warn("Time To has been cleared as it was before the new Time From");
                  }
                }
                this.setState({
                  newSchedule: {
                    ...newSchedule,
                    timeFrom: item,
                    timeTo: updatedTimeTo,
                  },
                });
              }}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              Time To *
            </Tag>
            <SingleSelectWrapper
              key={`timeTo-${formResetKey}`}
              items={timeOptions}
              selectedItem={newSchedule.timeTo}
              onChange={(item) => {
                const fromT = newSchedule.timeFrom ? moment(newSchedule.timeFrom, "h:mm A") : null;
                const toT = item ? moment(item, "h:mm A") : null;
                if (fromT && toT && toT.isBefore(fromT)) {
                  cogoToast.error("Time To cannot be before Time From");
                  return;
                }
                this.setState({
                  newSchedule: {
                    ...newSchedule,
                    timeTo: item,
                  },
                });
              }}
            />
          </ControlGroup>

          <ControlGroup>
            <Tag large minimal>
              Surgery Count
            </Tag>
            <NumericInput
              value={newSchedule.surgeryCount}
              onValueChange={(value) => {
                this.setState({
                  newSchedule: {
                    ...newSchedule,
                    surgeryCount: value || 0,
                  },
                });
              }}
              min={0}
              max={20}
              style={{ width: "100px" }}
            />
          </ControlGroup>

          <div style={{ display: "flex", gap: "15px", alignItems: "flex-end" }}>
            <ControlGroup>
              <Tag large minimal>
                Effective From *
              </Tag>
              <DateInput
                key={`effectiveFrom-${formResetKey}`}
                value={newSchedule.effectiveFrom ? moment(newSchedule.effectiveFrom).toDate() : null}
                onChange={(date) => {
                  const fromDate = date ? moment(date).format("YYYY-MM-DD") : null;
                  const toDate = newSchedule.effectiveTo;

                  // If effective to is before the new effective from, clear it
                  let updatedToDate = toDate;
                  if (fromDate && toDate && moment(toDate).isBefore(moment(fromDate))) {
                    updatedToDate = null;
                    cogoToast.warn("Effective To date has been cleared as it was before the new Effective From date");
                  }

                  this.setState({
                    newSchedule: {
                      ...newSchedule,
                      effectiveFrom: fromDate,
                      effectiveTo: updatedToDate,
                    },
                  });
                }}
                formatDate={(date) => moment(date).format("DD/MM/YYYY")}
                parseDate={(str) => moment(str, "DD/MM/YYYY").toDate()}
                placeholder="Select date"
                intent={!isDateRangeValid ? "danger" : "none"}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Effective To *
              </Tag>
              <DateInput
                key={`effectiveTo-${formResetKey}`}
                value={newSchedule.effectiveTo ? moment(newSchedule.effectiveTo).toDate() : null}
                onChange={(date) => {
                  const toDate = date ? moment(date).format("YYYY-MM-DD") : null;
                  const fromDate = newSchedule.effectiveFrom;

                  // Validate that effective to is not before effective from
                  if (fromDate && toDate && moment(toDate).isBefore(moment(fromDate))) {
                    cogoToast.error("Effective To date cannot be before Effective From date");
                    return; // Don't update the state if validation fails
                  }

                  this.setState({
                    newSchedule: {
                      ...newSchedule,
                      effectiveTo: toDate,
                    },
                  });
                }}
                formatDate={(date) => moment(date).format("DD/MM/YYYY")}
                parseDate={(str) => moment(str, "DD/MM/YYYY").toDate()}
                placeholder="Select date"
                intent={!isDateRangeValid ? "danger" : "none"}
              />
            </ControlGroup>
          </div>

          {/* Time range validation warning */}
          {!isTimeRangeValid && (
            <div
              style={{
                marginTop: "10px",
                padding: "8px 12px",
                backgroundColor: "#db3737",
                color: "white",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              ⚠️ Time To cannot be before Time From
            </div>
          )}

          {/* Date range validation warning */}
          {!isDateRangeValid && (
            <div
              style={{
                marginTop: "10px",
                padding: "8px 12px",
                backgroundColor: "#db3737",
                color: "white",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            >
              ⚠️ Effective To date cannot be before Effective From date
            </div>
          )}
        </div>

        {/* Surgery Groups - placed just above the Add Schedule button */}
        <div style={{ marginBottom: "20px" }}>
          <ControlGroup>
            <Tag large minimal>
              Surgery Groups
            </Tag>
            <div style={{ minWidth: "380px" }}>
              <MultiSelect
                items={(this.state.groups || []).map((g) => g.groupName)}
                itemPredicate={this.filterGroupItems}
                itemRenderer={this.renderGroupItem}
                onItemSelect={(item) => {
                  const current = this.state.newSchedule.surgeryGroups || [];
                  const exists = current.includes(item);
                  const next = exists ? current.filter((g) => g !== item) : [...current, item];
                  this.setState({
                    newSchedule: {
                      ...newSchedule,
                      surgeryGroups: next,
                    },
                  });
                }}
                selectedItems={newSchedule.surgeryGroups || []}
                tagRenderer={this.renderGroupTag}
                tagInputProps={{
                  placeholder:
                    (newSchedule.surgeryGroups || []).length === 0 ? "Search and select surgery groups..." : "",
                  large: true,
                }}
                noResults={<MenuItem disabled text="No groups found" />}
                popoverProps={{ minimal: true }}
              />
            </div>
          </ControlGroup>
        </div>

        <Button
          large
          intent="primary"
          icon="add"
          onClick={this.addSchedule}
          loading={this.state.isSaving}
          disabled={!isDateRangeValid || !isTimeRangeValid}
        >
          Add Schedule
        </Button>
      </Card>
    );
  };

  renderEditModal = () => {
    const { isEditModalOpen, editingSchedule, doctors, branches, daysOfWeek, timeOptions, isSaving } = this.state;

    if (!isEditModalOpen || !editingSchedule) return null;

    const isEditDateRangeValid = this.isEditDateRangeValid();
    const isEditTimeRangeValid = this.isEditTimeRangeValid();

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={this.closeEditModal}
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "800px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Edit Doctor Schedule</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>Update the schedule details below</p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <ControlGroup>
              <Tag large minimal>
                Doctor *
              </Tag>
              <SingleIDSelectWrapper
                items={doctors}
                selectedItem={doctors.find((d) => d._id === editingSchedule.doctorId)}
                labelKey="searchName"
                onChange={(item) => {
                  this.setState({
                    editingSchedule: {
                      ...editingSchedule,
                      doctorId: item._id,
                      doctorName: item.name,
                    },
                  });
                }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Branch *
              </Tag>
              <SingleSelectWrapper
                items={branches}
                selectedItem={editingSchedule.branchName}
                onChange={(item) => {
                  this.setState({
                    editingSchedule: {
                      ...editingSchedule,
                      branchName: item,
                    },
                  });
                }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Day *
              </Tag>
              <SingleSelectWrapper
                items={daysOfWeek}
                selectedItem={editingSchedule.day}
                onChange={(item) => {
                  this.setState({
                    editingSchedule: {
                      ...editingSchedule,
                      day: item,
                    },
                  });
                }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Surgery Groups
              </Tag>
              <div style={{ minWidth: "380px" }}>
                <MultiSelect
                  items={(this.state.groups || []).map((g) => g.groupName)}
                  itemPredicate={this.filterGroupItems}
                  itemRenderer={this.renderGroupItem}
                  onItemSelect={(item) => {
                    const current = this.state.editingSchedule?.surgeryGroups || [];
                    const exists = current.includes(item);
                    const next = exists ? current.filter((g) => g !== item) : [...current, item];
                    this.setState({
                      editingSchedule: {
                        ...editingSchedule,
                        surgeryGroups: next,
                      },
                    });
                  }}
                  selectedItems={editingSchedule.surgeryGroups || []}
                  tagRenderer={this.renderGroupTag}
                  tagInputProps={{
                    placeholder:
                      (editingSchedule.surgeryGroups || []).length === 0 ? "Search and select surgery groups..." : "",
                    large: true,
                  }}
                  noResults={<MenuItem disabled text="No groups found" />}
                  popoverProps={{ minimal: true }}
                />
              </div>
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Time From *
              </Tag>
              <SingleSelectWrapper
                items={timeOptions}
                selectedItem={editingSchedule.timeFrom}
                onChange={(item) => {
                  let updatedTimeTo = editingSchedule.timeTo;
                  if (updatedTimeTo) {
                    const fromT = moment(item, "h:mm A");
                    const toT = moment(updatedTimeTo, "h:mm A");
                    if (toT.isBefore(fromT)) {
                      updatedTimeTo = "";
                      cogoToast.warn("Time To has been cleared as it was before the new Time From");
                    }
                  }
                  this.setState({
                    editingSchedule: {
                      ...editingSchedule,
                      timeFrom: item,
                      timeTo: updatedTimeTo,
                    },
                  });
                }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Time To *
              </Tag>
              <SingleSelectWrapper
                items={timeOptions}
                selectedItem={editingSchedule.timeTo}
                onChange={(item) => {
                  const fromT = editingSchedule.timeFrom ? moment(editingSchedule.timeFrom, "h:mm A") : null;
                  const toT = item ? moment(item, "h:mm A") : null;
                  if (fromT && toT && toT.isBefore(fromT)) {
                    cogoToast.error("Time To cannot be before Time From");
                    return; // Don't update the state if validation fails
                  }
                  this.setState({
                    editingSchedule: {
                      ...editingSchedule,
                      timeTo: item,
                    },
                  });
                }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Surgery Count
              </Tag>
              <NumericInput
                value={editingSchedule.surgeryCount}
                onValueChange={(value) => {
                  this.setState({
                    editingSchedule: {
                      ...editingSchedule,
                      surgeryCount: value || 0,
                    },
                  });
                }}
                min={0}
                max={20}
                style={{ width: "100px" }}
              />
            </ControlGroup>

            <div style={{ display: "flex", gap: "15px", alignItems: "flex-end" }}>
              <ControlGroup>
                <Tag large minimal>
                  Effective From *
                </Tag>
                <DateInput
                  value={editingSchedule.effectiveFrom ? moment(editingSchedule.effectiveFrom).toDate() : null}
                  onChange={(date) => {
                    const fromDate = date ? moment(date).format("YYYY-MM-DD") : null;
                    const toDate = editingSchedule.effectiveTo;

                    // If effective to is before the new effective from, clear it
                    let updatedToDate = toDate;
                    if (fromDate && toDate && moment(toDate).isBefore(moment(fromDate))) {
                      updatedToDate = null;
                      cogoToast.warn("Effective To date has been cleared as it was before the new Effective From date");
                    }

                    this.setState({
                      editingSchedule: {
                        ...editingSchedule,
                        effectiveFrom: fromDate,
                        effectiveTo: updatedToDate,
                      },
                    });
                  }}
                  formatDate={(date) => moment(date).format("DD/MM/YYYY")}
                  parseDate={(str) => moment(str, "DD/MM/YYYY").toDate()}
                  placeholder="Select date"
                  intent={!isEditDateRangeValid ? "danger" : "none"}
                />
              </ControlGroup>

              <ControlGroup>
                <Tag large minimal>
                  Effective To *
                </Tag>
                <DateInput
                  value={editingSchedule.effectiveTo ? moment(editingSchedule.effectiveTo).toDate() : null}
                  onChange={(date) => {
                    const toDate = date ? moment(date).format("YYYY-MM-DD") : null;
                    const fromDate = editingSchedule.effectiveFrom;

                    // Validate that effective to is not before effective from
                    if (fromDate && toDate && moment(toDate).isBefore(moment(fromDate))) {
                      cogoToast.error("Effective To date cannot be before Effective From date");
                      return; // Don't update the state if validation fails
                    }

                    this.setState({
                      editingSchedule: {
                        ...editingSchedule,
                        effectiveTo: toDate,
                      },
                    });
                  }}
                  formatDate={(date) => moment(date).format("DD/MM/YYYY")}
                  parseDate={(str) => moment(str, "DD/MM/YYYY").toDate()}
                  placeholder="Select date"
                  intent={!isEditDateRangeValid ? "danger" : "none"}
                />
              </ControlGroup>
            </div>

            {/* Time range validation warning */}
            {!isEditTimeRangeValid && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "8px 12px",
                  backgroundColor: "#db3737",
                  color: "white",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                ⚠️ Time To cannot be before Time From
              </div>
            )}

            {/* Date range validation warning */}
            {!isEditDateRangeValid && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "8px 12px",
                  backgroundColor: "#db3737",
                  color: "white",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                ⚠️ Effective To date cannot be before Effective From date
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button large intent="none" icon="cross" onClick={this.closeEditModal}>
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.updateSchedule}
              loading={isSaving}
              disabled={!isEditDateRangeValid || !isEditTimeRangeValid}
            >
              Update Schedule
            </Button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { schedules, isLoading, quickFilterText } = this.state;

    if (isLoading) {
      return (
        <NonIdealState
          icon={<Spinner />}
          title="Loading Doctor Schedules..."
          description="Please wait while we load the doctor schedule data"
        />
      );
    }

    return (
      <div
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Doctor Schedule Master</H1>
          <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
            Manage doctor schedules, time slots, and capacity across different branches
          </p>
        </div>

        {this.renderAddForm()}

        <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
          <div style={{ marginBottom: "15px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Schedule List</H3>
            <ControlGroup>
              <Tag large minimal>
                Search
              </Tag>
              <InputGroup
                placeholder="Search schedules..."
                value={quickFilterText}
                onChange={(e) => this.setState({ quickFilterText: e.target.value })}
                style={{ width: "300px" }}
              />
            </ControlGroup>
          </div>

          <div className="ag-theme-alpine-dark" style={{ height: "500px", width: "100%" }}>
            <AgGridReact
              columnDefs={this.columnDefs}
              rowData={schedules}
              onGridReady={this.onGridReady}
              quickFilterText={quickFilterText}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
              }}
              rowSelection="single"
              enableRangeSelection={true}
              enableCharts={true}
              sideBar={true}
              statusBar={{
                statusPanels: [
                  {
                    statusPanel: "agTotalAndFilteredRowCountComponent",
                    align: "left",
                  },
                ],
              }}
              loading={isLoading}
            />
          </div>
        </Card>

        {/* Edit Modal */}
        {this.renderEditModal()}
      </div>
    );
  }
}

export default inject("authStore")(observer(DoctorScheduleMaster));
